import React, { useEffect, useState } from 'react';
import { Card, Checkbox, Row, Col, Typography, Space, Tooltip, Skeleton } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPortfoliosInBatches, fetchDetailedPortfolioObjects } from '../../../../api/portfolioApi';
import { setFormData } from '../../../../store/slices/path.slice';

const GridSelector = ({
  portfolioIds = [],
  maxSelect = 1,
  value = [],
  onChange,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const portfolios = useSelector((state) => state.portfolios.items);

  useEffect(() => {
    const loadPortfolios = async () => {
      setLoading(true);
      try {
        await dispatch(fetchPortfoliosInBatches({ portfolioIds }));
        await dispatch(fetchDetailedPortfolioObjects(portfolioIds));
      } catch (error) {
        console.error('Error loading portfolios:', error);
      }
      setLoading(false);
    };

    if (portfolioIds.length > 0) {
      loadPortfolios();
    }
  }, [portfolioIds, dispatch]);

  const handlePortfolioSelect = (portfolioId) => {
    let newValue;
    if (value.includes(portfolioId)) {
      newValue = value.filter((id) => id !== portfolioId);
    } else if (value.length < maxSelect) {
      newValue = [...value, portfolioId];
    } else {
      return;
    }

    onChange(newValue);
    dispatch(setFormData({ custom_portfolio_ids: newValue }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse">Loading portfolios...</div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="text-sm text-gray-500 mb-4">
        Select up to {maxSelect} portfolio{maxSelect !== 1 ? 's' : ''}
      </div>

      <Row gutter={[16, 16]}>
        {portfolioIds.map((id) => {
          const portfolio = portfolios.find((p) => p.id === id);
          if (!portfolio) return null;

          const summary = portfolio.attr?.summary;
          const hasSummary = summary && summary.trim().length > 0;

          return (
            <Col key={id} xs={24} lg={12}>
              <Card
                hoverable
                className={`h-48 overflow-hidden transition-all duration-300 ${
                  value.includes(id)
                    ? 'border-2 border-emerald-500 shadow-lg'
                    : 'border border-gray-200'
                }`}
                onClick={() => handlePortfolioSelect(id)}
              >
                <Space direction="vertical" className="w-full h-full">
                  <div className="flex items-start gap-3">
                    <Checkbox checked={value.includes(id)} className="mt-1" />
                    <div className="flex-1">
                      <Typography.Title level={5} className="m-0 line-clamp-1">
                        {portfolio.name}
                      </Typography.Title>

                      {/* Show a Skeleton if summary is empty or missing */}
                      {hasSummary ? (
                        <div className="mt-4">
                          <Tooltip
                            title={summary}
                            placement="bottom"
                            overlayStyle={{ maxWidth: '400px' }}
                          >
                            <Typography.Paragraph className="text-sm text-gray-600 mt-1 mb-0 line-clamp-4">
                              {summary}
                            </Typography.Paragraph>
                          </Tooltip>
                        </div>
                      ) : (
                        <div className="mt-4">
                          <Skeleton active paragraph={{ rows: 2 }} />
                        </div>
                      )}
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default GridSelector;
